import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import useGuides from '../hooks/useGuides';
import { VISA_COUNTRIES, VISA_RULE_LABELS } from '../data/tools';
import renderGuide from '../utils/renderGuide';
import '../styles/Dashboard.css';

const TABS = [
  { id: 'overview', label: { en: 'Overview', fr: 'Aperçu' }, icon: '🏠' },
  { id: 'guides',   label: { en: 'Guides', fr: 'Guides' }, icon: '📚' },
  { id: 'visa',     label: { en: 'Visa Checker', fr: 'Vérif. visa' }, icon: '🛂' },
  { id: 'checklist',label: { en: 'Checklist', fr: 'Liste de contrôle' }, icon: '✅' },
  { id: 'profile',  label: { en: 'Profile', fr: 'Profil' }, icon: '👤' },
];

const CHECKLIST_ITEMS = [
  { id: 'passport', en: 'Passport valid 6+ months after return, 2 blank pages', fr: 'Passeport valide 6+ mois après le retour, 2 pages vierges' },
  { id: 'visa_rule', en: 'Checked my visa rule (visa-free / e-Visa / embassy)', fr: 'Règle de visa vérifiée (sans visa / e-Visa / ambassade)' },
  { id: 'bookings', en: 'Refundable flight and hotel booked', fr: 'Vol et hôtel remboursables réservés' },
  { id: 'bank', en: 'Bank statements (3–6 months) printed', fr: 'Relevés bancaires (3–6 mois) imprimés' },
  { id: 'insurance', en: 'Travel insurance with €30,000+ coverage', fr: 'Assurance voyage avec couverture 30 000 €+' },
  { id: 'visa', en: 'Visa obtained (official channel only)', fr: 'Visa obtenu (canal officiel uniquement)' },
  { id: 'euros', en: 'Euros in cash for exchange on arrival', fr: 'Euros en espèces pour le change à l\'arrivée' },
  { id: 'esim', en: 'eSIM bought or SIM plan chosen', fr: 'eSIM achetée ou forfait SIM choisi' },
  { id: 'transfer', en: 'Airport transfer planned (M11, Havaist or taxi)', fr: 'Transfert aéroport prévu (M11, Havaist ou taxi)' },
  { id: 'copies', en: 'Copies of passport, visa and bookings saved', fr: 'Copies du passeport, visa et réservations sauvegardées' },
];


const BasicDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const lang = localStorage.getItem('ig_language') || 'en';
  const { guides, loading: guidesLoading, error: guidesError, expired, countAt, lockedAt } = useGuides();
  const guideStatus = guidesLoading
    ? <p className="db-panel-note">⏳ {lang === 'fr' ? 'Chargement des guides…' : 'Loading guides…'}</p>
    : guidesError
      ? <p className="db-panel-note">⚠️ {lang === 'fr' ? 'Impossible de charger les guides. Vérifiez votre connexion et rechargez la page.' : 'Could not load guides. Check your connection and reload the page.'}</p>
      : expired
        ? <p className="db-panel-note">⌛ {lang === 'fr' ? 'Votre accès a expiré. Renouvelez votre forfait pour retrouver tous vos guides.' : 'Your access has expired. Renew your plan to get all your guides back.'}</p>
        : null;
  const BASIC_GUIDES = guides;
  const EXTRA_WITH_NAVIGATOR = countAt('premium') - countAt('basic');

  const [tab, setTab] = useState('overview');
  const [selectedGuide, setSelectedGuide] = useState(null);
  const [checked, setChecked] = useState({});
  const [visaCountry, setVisaCountry] = useState(() => {
    const c = (user?.country || '').toUpperCase();
    return VISA_COUNTRIES.some(v => v.code === c) ? c : 'CM';
  });

  useEffect(() => {
    const saved = localStorage.getItem('ig_checklist');
    if (saved) setChecked(JSON.parse(saved));
  }, []);

  const toggleCheck = (id) => {
    const next = { ...checked, [id]: !checked[id] };
    setChecked(next);
    localStorage.setItem('ig_checklist', JSON.stringify(next));
  };

  const completedCount = Object.values(checked).filter(Boolean).length;

  const OverviewTab = () => (
    <div className="db-overview">
      <div className="db-welcome-card">
        <div className="db-welcome-icon">🕌</div>
        <div>
          <h2>{lang === 'fr' ? `Bienvenue, ${user?.firstName || 'Explorateur'} !` : `Welcome, ${user?.firstName || 'Explorer'}!`}</h2>
          <p>{lang === 'fr' ? 'Votre voyage à Istanbul commence ici.' : 'Your Istanbul trip starts here.'}</p>
        </div>
        <div className="db-plan-badge basic">{lang === 'fr' ? 'Explorateur' : 'Explorer'}</div>
      </div>

      <div className="db-stats-row">
        <div className="db-stat-card">
          <div className="db-stat-num">{BASIC_GUIDES.length}</div>
          <div className="db-stat-label">{lang === 'fr' ? 'Guides Disponibles' : 'Guides Available'}</div>
        </div>
        <div className="db-stat-card">
          <div className="db-stat-num">{completedCount}/{CHECKLIST_ITEMS.length}</div>
          <div className="db-stat-label">{lang === 'fr' ? 'Tâches Complétées' : 'Tasks Completed'}</div>
        </div>
        <div className="db-stat-card">
          <div className="db-stat-num">30</div>
          <div className="db-stat-label">{lang === 'fr' ? 'Jours d\'Accès' : 'Days of Access'}</div>
        </div>
      </div>

      <div className="db-info-panel">
        <h3>🛂 {lang === 'fr' ? 'Visa Turquie — l\'essentiel' : 'Turkey Visa — Key Facts'}</h3>
        <div className="db-fact-grid">
          <div className="db-fact"><span>{lang === 'fr' ? 'Ambassade' : 'Embassy'}</span><p>{lang === 'fr' ? 'Cameroun, Côte d\'Ivoire, Gabon, Congo…' : 'Cameroon, Côte d\'Ivoire, Gabon, Congo…'}</p></div>
          <div className="db-fact"><span>e-Visa*</span><p>{lang === 'fr' ? 'Sénégal (avec visa Schengen/US/UK)' : 'Senegal (with Schengen/US/UK visa)'}</p></div>
          <div className="db-fact"><span>{lang === 'fr' ? 'Sans visa' : 'Visa-free'}</span><p>{lang === 'fr' ? 'Maroc, Tunisie (90 j)' : 'Morocco, Tunisia (90 days)'}</p></div>
          <div className="db-fact"><span>evisa.gov.tr</span><p>{lang === 'fr' ? 'Seul site officiel' : 'Only official site'}</p></div>
        </div>
      </div>

      <div className="db-info-panel green">
        <h3>🚇 {lang === 'fr' ? 'Se déplacer — tarifs 2026' : 'Getting Around — 2026 Fares'}</h3>
        <div className="db-fact-grid">
          <div className="db-fact"><span>165 TL</span><p>{lang === 'fr' ? 'Carte Istanbulkart' : 'Istanbulkart card'}</p></div>
          <div className="db-fact"><span>42 TL</span><p>{lang === 'fr' ? 'Trajet métro / tram / bus' : 'Metro / tram / bus ride'}</p></div>
          <div className="db-fact"><span>~55 TL</span><p>{lang === 'fr' ? 'Métro M11 depuis l\'aéroport' : 'M11 metro from airport'}</p></div>
          <div className="db-fact"><span>426 TL</span><p>{lang === 'fr' ? 'Havaist aéroport → Taksim' : 'Havaist airport → Taksim'}</p></div>
        </div>
      </div>

      <div className="db-table-panel">
        <h3>🏛️ {lang === 'fr' ? 'Tarifs d\'entrée 2026 (étrangers)' : '2026 Entrance Fees (foreign visitors)'}</h3>
        <div className="db-table-wrap">
          <table className="db-table">
            <thead><tr><th>{lang === 'fr' ? 'Site' : 'Attraction'}</th><th>{lang === 'fr' ? 'Prix' : 'Price'}</th><th>≈ XAF</th></tr></thead>
            <tbody>
              <tr><td>{lang === 'fr' ? 'Sainte-Sophie' : 'Hagia Sophia'}</td><td>€25</td><td>16,400</td></tr>
              <tr><td>{lang === 'fr' ? 'Palais de Topkapı + Harem' : 'Topkapı Palace + Harem'}</td><td>2,750 TL</td><td>32,200</td></tr>
              <tr><td>{lang === 'fr' ? 'Citerne Basilique (jour)' : 'Basilica Cistern (day)'}</td><td>1,950 TL</td><td>22,800</td></tr>
              <tr><td>{lang === 'fr' ? 'Palais de Dolmabahçe' : 'Dolmabahçe Palace'}</td><td>2,000 TL</td><td>23,400</td></tr>
              <tr><td>{lang === 'fr' ? 'Tour de Galata' : 'Galata Tower'}</td><td>€30</td><td>19,700</td></tr>
              <tr><td>{lang === 'fr' ? 'Mosquée Bleue' : 'Blue Mosque'}</td><td>{lang === 'fr' ? 'Gratuit' : 'Free'}</td><td>—</td></tr>
              <tr><td>Museum Pass (5 {lang === 'fr' ? 'jours' : 'days'})</td><td>€105</td><td>68,900</td></tr>
            </tbody>
          </table>
        </div>
        <p className="db-panel-note">{lang === 'fr' ? 'Taux utilisé : 1 € ≈ 56 TL (sept. 2026). Les prix en livres augmentent souvent.' : 'Rate used: €1 ≈ 56 TL (Sept 2026). Lira prices rise often.'}</p>
      </div>

      <div className="db-upgrade-banner">
        <div>
          <h4>{lang === 'fr' ? `Débloquez ${EXTRA_WITH_NAVIGATOR} guides supplémentaires` : `Unlock ${EXTRA_WITH_NAVIGATOR} More Guides`}</h4>
          <p>{lang === 'fr' ? 'Passez à Navigateur : itinéraires, Bosphore & îles, Cappadoce, hammams, shopping, études, calculateur de budget, comparatif des quartiers.' : 'Upgrade to Navigator: itineraries, Bosphorus & islands, Cappadocia, hammams, shopping, study, budget calculator, district comparison.'}</p>
        </div>
        <button className="db-upgrade-btn" onClick={() => navigate('/plans')}>
          {lang === 'fr' ? '⬆️ Passer à Navigateur' : '⬆️ Upgrade to Navigator'}
        </button>
      </div>
    </div>
  );

  const GuidesTab = () => (
    <div className="db-guides">
      {selectedGuide ? (
        <div className="db-guide-reader">
          <button className="db-back-btn" onClick={() => setSelectedGuide(null)}>← {lang === 'fr' ? 'Retour' : 'Back'}</button>
          <div className="db-guide-icon-lg">{selectedGuide.icon}</div>
          <h2>{selectedGuide.title[lang] || selectedGuide.title.en}</h2>
          <div className="db-guide-content" dangerouslySetInnerHTML={{ __html: renderGuide(selectedGuide.content[lang] || selectedGuide.content.en) }} />
        </div>
      ) : (
        <>
          <h2 className="db-section-title">{lang === 'fr' ? 'Vos guides de voyage' : 'Your Travel Guides'}</h2>
          {guideStatus}
          <div className="db-guides-grid">
            {BASIC_GUIDES.map(guide => (
              <div key={guide.id} className="db-guide-card" onClick={() => setSelectedGuide(guide)}>
                <div className="db-guide-icon">{guide.icon}</div>
                <div className="db-guide-info">
                  <h4>{guide.title[lang] || guide.title.en}</h4>
                  <p>{guide.description[lang] || guide.description.en}</p>
                </div>
                <span className="db-guide-arrow">→</span>
              </div>
            ))}
          </div>
          <div className="db-locked-section">
            <div className="db-locked-header">🔒 {lang === 'fr' ? 'Guides Premium — Passez à Navigateur' : 'Premium Guides — Upgrade to Navigator'}</div>
            {lockedAt('premium').map(guide => (
              <div key={guide.id} className="db-guide-card locked">
                <div className="db-guide-icon locked-icon">{guide.icon}</div>
                <div className="db-guide-info">
                  <h4>{guide.title[lang] || guide.title.en}</h4>
                  <p className="locked-desc">{lang === 'fr' ? 'Disponible avec Navigateur (16 500 XAF / 90 jours)' : 'Available with Navigator (16,500 XAF / 90 days)'}</p>
                </div>
                <span className="db-lock-badge">🔒</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );

  const VisaTab = () => {
    const c = VISA_COUNTRIES.find(v => v.code === visaCountry) || VISA_COUNTRIES[0];
    const rule = VISA_RULE_LABELS[c.rule];
    return (
      <div className="db-checklist">
        <h2 className="db-section-title">🛂 {lang === 'fr' ? 'Quel visa pour votre passeport ?' : 'Which Visa for Your Passport?'}</h2>
        <div className="db-tool-row">
          <label htmlFor="visa-country">{lang === 'fr' ? 'Votre nationalité' : 'Your nationality'}</label>
          <select id="visa-country" className="db-tool-select" value={visaCountry} onChange={e => setVisaCountry(e.target.value)}>
            {VISA_COUNTRIES.map(v => <option key={v.code} value={v.code}>{v.name[lang] || v.name.en}</option>)}
          </select>
        </div>
        <div className={`db-info-panel ${rule.tone}`}>
          <h3>{rule.icon} {rule[lang] || rule.en}</h3>
          <p><strong>{lang === 'fr' ? 'Où faire la demande :' : 'Where to apply:'}</strong> {c.where[lang] || c.where.en}</p>
        </div>
        <div className="db-table-panel">
          <h3>📋 {lang === 'fr' ? 'Ce qu\'il vous faut' : 'What you need'}</h3>
          <div className="db-dd-list">
            {(c.docs[lang] || c.docs.en).map((d, i) => (
              <div key={i} className="db-dd-item"><span className="db-dd-num">{i + 1}</span><span>{d}</span></div>
            ))}
          </div>
        </div>
        <p className="db-panel-note">{lang === 'fr' ? 'Règles vérifiées en septembre 2026. Elles peuvent changer : confirmez toujours sur evisa.gov.tr ou auprès de l\'ambassade avant de payer. Détails complets dans les guides « Visa » et « Visa refusé ? ».' : 'Rules checked September 2026. They can change: always confirm on evisa.gov.tr or with the embassy before paying. Full details in the "Visa" and "Visa Refused?" guides.'}</p>
      </div>
    );
  };

  const ChecklistTab = () => (
    <div className="db-checklist">
      <h2 className="db-section-title">{lang === 'fr' ? 'Liste de préparation au voyage' : 'Trip Preparation Checklist'}</h2>
      <div className="db-progress-bar">
        <div className="db-progress-fill" style={{ width: `${(completedCount / CHECKLIST_ITEMS.length) * 100}%` }} />
      </div>
      <p className="db-progress-label">{completedCount} / {CHECKLIST_ITEMS.length} {lang === 'fr' ? 'tâches complétées' : 'tasks completed'}</p>
      <div className="db-checklist-items">
        {CHECKLIST_ITEMS.map(item => (
          <div key={item.id} className={`db-checklist-item ${checked[item.id] ? 'done' : ''}`} onClick={() => toggleCheck(item.id)}>
            <div className={`db-checkbox ${checked[item.id] ? 'checked' : ''}`}>
              {checked[item.id] && '✓'}
            </div>
            <span>{item[lang] || item.en}</span>
          </div>
        ))}
      </div>
    </div>
  );

  const ProfileTab = () => (
    <div className="db-profile">
      <div className="db-profile-card">
        <div className="db-profile-avatar">{user?.firstName?.[0]}{user?.lastName?.[0]}</div>
        <h2>{user?.firstName} {user?.lastName}</h2>
        <p>{user?.email}</p>
        <div className="db-plan-badge basic">{lang === 'fr' ? 'Forfait Explorateur' : 'Explorer Plan'}</div>
      </div>
      <div className="db-profile-details">
        <div className="db-detail-row"><span>{lang === 'fr' ? 'Pays d\'origine' : 'Country of origin'}</span><strong>{user?.country || '—'}</strong></div>
        <div className="db-detail-row"><span>{lang === 'fr' ? 'Numéro de téléphone' : 'Phone number'}</span><strong>{user?.phone || '—'}</strong></div>
        <div className="db-detail-row"><span>{lang === 'fr' ? 'Forfait actuel' : 'Current plan'}</span><strong>{lang === 'fr' ? 'Explorateur (Basique)' : 'Explorer (Basic)'}</strong></div>
        <div className="db-detail-row"><span>{lang === 'fr' ? 'Accès' : 'Access'}</span><strong>{lang === 'fr' ? '30 jours' : '30 days'}</strong></div>
      </div>
      <div className="db-profile-actions">
        <button className="db-upgrade-btn" onClick={() => navigate('/plans')}>{lang === 'fr' ? '⬆️ Passer à Navigateur' : '⬆️ Upgrade to Navigator'}</button>
        <button className="db-logout-btn" onClick={logout}>{lang === 'fr' ? 'Se Déconnecter' : 'Sign Out'}</button>
      </div>
    </div>
  );

  const renderTab = () => {
    switch (tab) {
      case 'overview':  return <OverviewTab />;
      case 'guides':    return <GuidesTab />;
      case 'visa':      return VisaTab();
      case 'checklist': return <ChecklistTab />;
      case 'profile':   return <ProfileTab />;
      default:          return <OverviewTab />;
    }
  };

  return (
    <div className="db-layout">
      <aside className="db-sidebar">
        <div className="db-sidebar-brand">
          <span className="db-brand-icon">🕌</span>
          <span className="db-brand-text">{lang === 'fr' ? 'Guide Istanbul' : 'Istanbul Guide'}</span>
        </div>
        <nav className="db-nav">
          {TABS.map(t => (
            <button key={t.id} className={`db-nav-item ${tab === t.id ? 'active' : ''}`} onClick={() => setTab(t.id)}>
              <span className="db-nav-icon">{t.icon}</span>
              <span>{t.label[lang] || t.label.en}</span>
            </button>
          ))}
        </nav>
        <div className="db-sidebar-footer">
          <button className="db-signout-btn" onClick={logout}>{lang === 'fr' ? 'Déconnexion' : 'Sign Out'}</button>
        </div>
      </aside>
      <main className="db-main">
        <div className="db-topbar">
          <h1 className="db-page-title">
            {TABS.find(t => t.id === tab)?.icon} {TABS.find(t => t.id === tab)?.label[lang]}
          </h1>
          <div className="db-user-chip">
            <div className="db-user-avatar">{user?.firstName?.[0]}</div>
            <span>{user?.firstName}</span>
          </div>
        </div>
        <div className="db-content">
          {renderTab()}
        </div>
      </main>
    </div>
  );
};

export default BasicDashboard;
